import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import * as api from "../services/mockAPI";
import { FloorPlan, SeatStatus } from "../types";
import { FloorPlanViewer } from "../components/FloorPlanViewer";
import { Calendar } from "../components/Calendar";
import { useNotification } from "../context/NotificationContext";
import "./ReservationPage.css";

const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
}

const getInitialDate = (searchParams: URLSearchParams): Date => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
        // Add "T00:00:00" to ensure the date is parsed in the local timezone, not UTC
        const date = new Date(dateParam + "T00:00:00");
        if (!isNaN(date.getTime())) {
            return date;
        }
    }
    return new Date();
}

export const ReservationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, reservations, addReservation, cancelReservation } = useData();
  const { addNotification } = useNotification();
  const [selectedDate, setSelectedDate] = useState(() => getInitialDate(searchParams));
  
  const [planData, setPlanData] = useState<FloorPlan | null>(null);
  const [seatStatus, setSeatStatus] = useState<SeatStatus[]>([]);
  
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Read the focusedDeskId from the URL on initial render and whenever params change
  const focusedDeskId = useMemo(() => searchParams.get('deskId'), [searchParams]);

  // Effect 1: Fetch static layout data only ONCE.
  useEffect(() => {
    setIsPageLoading(true);
    api.getLayoutData("full-floor-plan-v2")
      .then(setPlanData)
      .catch(error => {
        addNotification("Could not load floor plan layout.", "error");
      })
      .finally(() => {
        setIsPageLoading(false);
      });
  }, [addNotification]);

  // Effect 2: Fetch dynamic seat status whenever the date or planData changes.
  useEffect(() => {
    if (!planData) return;

    setIsStatusUpdating(true);
    const dateString = formatDate(selectedDate);
    // Persist focusedDeskId in the URL when changing dates
    navigate(`/reserve?date=${dateString}${focusedDeskId ? `&deskId=${focusedDeskId}` : ''}`, { replace: true });

    api.getSeatStatus("full-floor-plan-v2", dateString)
      .then(setSeatStatus)
      .catch(error => {
        addNotification("Could not update desk statuses.", "error");
      })
      .finally(() => {
        setIsStatusUpdating(false);
      });
  }, [selectedDate, planData, addNotification, navigate, focusedDeskId]);

  const handleSeatClick = async (deskId: string) => {
    if (!user || !planData) return;

    const dateString = formatDate(selectedDate);
    const desk = planData.desks.find(d => d.id === deskId);
    if (!desk) return;

    const existingReservation = reservations.find(r => r.date === dateString);
    const clickedDeskStatus = seatStatus.find(s => s.seatId === deskId)?.status;

    // Scenario 1: Un-reserve (Clicking your own booked desk)
    if (existingReservation && existingReservation.deskId === deskId) {
        await cancelReservation(existingReservation.id);
        // DataContext handles state refresh
        return;
    }

    // Scenario 2: Change reservation (You have a booking, but click a different available desk)
    // Note: The logic in DataContext will now handle override.
    if (existingReservation && existingReservation.deskId !== deskId) {
        if (clickedDeskStatus === 'booked') {
            addNotification(`Desk ${desk.name} is already booked by another user.`, 'error');
            return;
        }
        await addReservation(deskId, dateString);
        return;
    }

    // Scenario 3: New reservation (No booking for this day)
    if (!existingReservation) {
        if (clickedDeskStatus === 'booked') {
             addNotification(`Desk ${desk.name} is already booked.`, 'error');
            return;
        }
        await addReservation(deskId, dateString);
        return;
    }
  };

  const changeDay = (amount: number) => {
    setSelectedDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(newDate.getDate() + amount);
        return newDate;
    });
  }

  const changeMonth = (amount: number) => {
    setSelectedDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(newDate.getMonth() + amount);
        return newDate;
    })
  }
  
  const handleDateSelectFromCalendar = (date: Date) => {
      setSelectedDate(date);
      setIsCalendarOpen(false);
  }

  const userReservationForDate = reservations.find(r => r.date === formatDate(selectedDate));

  return (
    <div>
      <h1>Reserve a Desk</h1>
      <div className="date-selector">
        <button className="date-nav-button" onClick={() => changeMonth(-1)}>« m</button>
        <button className="date-nav-button" onClick={() => changeDay(-1)}>« d</button>
        <button className="date-display-button" onClick={() => setIsCalendarOpen(true)}>
            {selectedDate.toDateString()}
        </button>
        <button className="date-nav-button" onClick={() => changeDay(1)}>d »</button>
        <button className="date-nav-button" onClick={() => changeMonth(1)}>m »</button>
      </div>

      {isCalendarOpen && (
        <Calendar 
          currentDate={selectedDate}
          onDateSelect={handleDateSelectFromCalendar}
          onClose={() => setIsCalendarOpen(false)}
        />
      )}
      
      {isPageLoading && <p>Loading floor plan...</p>}
      
      {planData && (
        <div className={`floorplan-wrapper ${isStatusUpdating ? 'updating' : ''}`}>
          <FloorPlanViewer
            planData={planData}
            statusData={seatStatus}
            onSeatClick={handleSeatClick}
            userReservationId={userReservationForDate?.deskId}
            focusedDeskId={focusedDeskId}
          />
        </div>
      )}
    </div>
  );
};